import { StyleSheet } from 'aphrodite';
import { headerHeight } from '../../../../../aida/styles/styles';
import {
	aceGray,
	background,
	black,
	brandPrimaryText,
	darkGrayFontColor,
	destructive,
	destructiveHover,
	destructiveHoverLight,
	gray3,
	gray4,
	mentionDark,
	titles,
	white,
} from '../../../../styles/colors';
import { CssSelectors, ctaButtonBase } from '../../../../styles/styles';

export const styles = StyleSheet.create({
	// Layout styles
	container: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		background,
	},
	flex: {
		display: 'flex',
		flex: 1,
	},
	leftContainer: {
		flex: 2,
		padding: 20,
		overflowY: 'auto',
		width: '100%',
	},
	rightContainer: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		padding: 16,
		justifyContent: 'space-between',
		backgroundColor: white,
		maxWidth: 360,
	},

	// Preview styles
	subject: {
		color: titles,
		fontSize: 16,
		marginRight: 30,
		marginTop: 10,
	},
	previewContainerHeader: {
		alignItems: 'center',
		borderBottom: `1px solid ${mentionDark}`,
		boxSizing: 'border-box',
		color: brandPrimaryText,
		display: 'flex',
		fontSize: 24,
		height: headerHeight,
		paddingBottom: 5,
		paddingLeft: 30,
		justifyContent: 'space-between',
	},
	previewContainer: {
		display: 'flex',
		flexDirection: 'row',
		gap: 10,
	},
	platformLabel: {
		alignItems: 'center',
		display: 'flex',
		fontSize: 14,
		fontWeight: 600,
		gap: 8,
		marginTop: 12,
	},
	platformLogo: {
		height: 20,
		width: 20,
	},
	previewContentBox: {
		background: white,
		borderRadius: 10,
		boxShadow: '0 2px 30px rgba(0, 0, 0, 0.15)',
		color: black,
		fontSize: 14,
		marginBottom: 20,
		marginTop: 12,
		opacity: 1,
		overflowWrap: 'break-word',
		transform: 'translate3d(0,0,0)',
		transition: 'opacity 0.2s, transform 0.5s',
		whiteSpace: 'pre-wrap',
		width: 372,
		wordBreak: 'break-word',
		wordWrap: 'break-word',
	},
	previewContentContainer: {
		alignItems: 'center',
		color: brandPrimaryText,
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		fontSize: 16,
		paddingTop: 30,
		...CssSelectors.allChildren(
			{
				color: brandPrimaryText,
			},
			'span'
		),
	},

	// Header and content styles
	header: {
		marginBottom: 12,
	},
	title: {
		fontSize: 18,
		fontWeight: 600,
		color: black,
		marginBottom: 4,
	},
	subtitle: {
		fontSize: 14,
		color: gray4,
		marginBottom: 8,
	},
	divider: {
		border: 0,
		height: 1,
		backgroundColor: aceGray,
	},
	disclaimerText: {
		fontSize: 12,
		color: darkGrayFontColor,
		textAlign: 'center',
	},

	// Button and footer styles
	footer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
	},
	ctaButton: {
		backgroundColor: brandPrimaryText,
		color: white,
		border: 'none',
		padding: '10px 20px',
		cursor: 'pointer',
		borderRadius: 4,
		':disabled': {
			backgroundColor: aceGray,
			cursor: 'not-allowed',
		},
	},
	linkDestructive: {
		backgroundColor: 'transparent',
		color: destructive,
		border: 'none',
		padding: '10px 20px',
		cursor: 'pointer',
		textDecoration: 'underline',
		':disabled': {
			color: aceGray,
			cursor: 'not-allowed',
		},
	},
	rejectButton: {
		...ctaButtonBase,
		':active': {
			background: destructiveHover,
		},
		':hover': {
			background: destructiveHoverLight,
			border: `1px solid ${destructiveHover}`,
		},
		transition: 'color .5s, background-color .5s',
		background: white,
		border: `1px solid ${destructive}`,
		boxShadow: '0 1px 2px 0 #f9f9f9',
		color: destructive,
	},

	// Modal styles
	modalContent: {
		alignItems: 'flex-end',
		backgroundColor: white,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		padding: 10,
		zIndex: 5,
	},
	modalOverlay: {
		alignItems: 'center',
		background: 'rgba(0, 0, 0, 0.5)',
		display: 'flex',
		inset: 0,
		height: '100%',
		justifyContent: 'center',
		position: 'fixed',
		width: '100%',
		zIndex: 5,
	},
	closeButton: {
		background: 'transparent',
		border: 'none',
		color: brandPrimaryText,
		cursor: 'pointer',
		fontSize: 16,
		marginBottom: 10,
	},
	previewImage: {
		width: '100%',
		height: 'auto',
		borderRadius: 10,
	},

	// Citation styles
	citationModal: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		background: white,
		padding: 20,
		borderRadius: 10,
		boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
		width: 400,
	},
	citationContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		overflowY: 'auto',
		wordBreak: 'break-word',
		maxHeight: 400,
	},
	citationTitle: {
		fontSize: 18,
		fontWeight: 'bold',
		marginBottom: 10,
	},
	citationContent: {
		fontSize: 14,
		color: gray3,
		marginBottom: 20,
		textAlign: 'center',
	},

	// Rejection styles
	rejectionInfo: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		padding: '12px 0',
	},
	rejectionReasonSection: {
		marginBottom: 16,
	},
	rejectionReasonTitle: {
		fontSize: 15,
		color: gray3,
		marginBottom: 8,
	},
	rejectionCategoryList: {
		listStyleType: 'disc',
		paddingLeft: 20,
		margin: '4px 0',
	},
	rejectionCategoryItem: {
		color: gray4,
		fontSize: 14,
		marginBottom: 4,
	},
	rejectionNotesSection: {
		marginBottom: 8,
	},
	rejectionNotesTitle: {
		fontSize: 15,
		color: gray3,
		marginBottom: 8,
	},
	rejectionNotesContent: {
		fontSize: 14,
		color: gray4,
		whiteSpace: 'pre-line',
		lineHeight: 1.4,
	},
});
